import type { EntitySnapshot, WeaponClass } from '../net/protocol';

/**
 * Sounds the server sends no event for, read off the snapshot instead. Pure, and fed once per
 * snapshot by `attachAudio`.
 *
 * Both could have been new events. They are not, because the snapshot already carries the fact and
 * an event per swing per player is traffic that scales with how busy a fight is — the reason
 * `damageType` omits its common case.
 */

/**
 * How far a basic attack's cooldown must **rise** between two snapshots to count as a swing.
 *
 * `cooldown` is normalised, 1 just after a swing and falling to 0 when the next is ready
 * (`PoseContext.cooldown`). It only ever rises at the moment a swing commits, so a rise of half its
 * range cannot be anything else — and a small rise (a stat change mid-swing rescaling the period)
 * is not mistaken for one.
 */
export const SWING_RISE = 0.5;

/** Which swing a weapon family makes. Bare hands make none: a fist has no whoosh worth a voice. */
export const SWING_SAMPLE: Readonly<Record<WeaponClass, string>> = {
  SWORD_1H: 'swing_sword',
  SWORD_2H: 'swing_sword',
  AXE_1H: 'swing_axe',
  AXE_2H: 'swing_axe',
  MACE_1H: 'swing_mace',
  MACE_2H: 'swing_mace',
  DAGGER: 'swing_dagger',
  WAND: 'swing_staff',
  STAFF: 'swing_staff',
  BOW: 'swing_bow',
};

export interface Swing {
  id: string;
  weapon: WeaponClass;
  at: { x: number; z: number };
}

/** Watches players' attack cooldowns for the rise that means a swing just committed. */
export class SwingWatch {
  private readonly last = new Map<string, number>();

  /** The players who swung since the previous snapshot. */
  step(entities: Iterable<EntitySnapshot>): Swing[] {
    const swings: Swing[] = [];
    const seen = new Set<string>();
    for (const e of entities) {
      if (e.kind !== 'player') continue;
      seen.add(e.id);
      const before = this.last.get(e.id);
      this.last.set(e.id, e.cooldown);
      // The first sighting has nothing to compare against: a player walking into view mid-swing
      // is not a swing heard.
      if (before === undefined || !e.weapon) continue;
      if (e.cooldown - before >= SWING_RISE) swings.push({ id: e.id, weapon: e.weapon, at: { x: e.x, z: e.z } });
    }
    for (const id of [...this.last.keys()]) if (!seen.has(id)) this.last.delete(id);
    return swings;
  }
}

/**
 * Watches this client's own zone for a change.
 *
 * `EVENT_ZONE_CHANGED` cannot say it: it is emitted into the destination instance, so everyone
 * already there receives it whenever anyone arrives, and its `entityId` is a layout kind. The seed
 * of the ZONE frame this client was sent is exactly *I arrived somewhere*. The first zone — the one
 * the game opens in — is not a change.
 */
export class ZoneWatch {
  private seed: number | null = null;

  step(seed: number | null | undefined): boolean {
    if (seed === null || seed === undefined) return false;
    const changed = this.seed !== null && seed !== this.seed;
    this.seed = seed;
    return changed;
  }
}
